import { useEffect } from "react";
import { catchError, tap, throwError, type Observable } from "rxjs";

import type {
  APILibraryTemplateMetadataRequestType,
  APILibraryTemplateRequestType,
} from "@/core/data_sources/models/APILibraryTemplate";
import { parseLinkHeader } from "@/core/utils/pagination";
import { Store } from "@/core/data_sources/store";
import { AppStore } from "@/core/data_sources/store/app";
import { useService } from "@/core/utils/hooks";
import { requestToObservable } from "@/core/utils/observables";

import {
  archiveTemplate,
  getTemplates,
  importTemplate,
  patchTemplate,
  publishTemplate,
} from "../services";
import { LibraryTemplatesState } from "./state";

import type { LibraryTemplatesActions } from "./actions";
import type { LibraryTemplatesHooks } from "./hooks";

export class LibraryTemplatesStore extends Store<
  LibraryTemplatesState,
  LibraryTemplatesActions,
  LibraryTemplatesHooks
> {
  private static _instance: LibraryTemplatesStore;

  static get instance() {
    LibraryTemplatesStore._instance =
      LibraryTemplatesStore._instance || new LibraryTemplatesStore();
    return this._instance;
  }
  constructor() {
    super(
      new LibraryTemplatesState(),
      {
        archiveTemplate: (id: number): Observable<unknown> => {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() => archiveTemplate(id)).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
        importTemplate: (
          template: APILibraryTemplateRequestType,
        ): Observable<unknown> => {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() => importTemplate(template)).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
        publishTemplate: (id: number): Observable<unknown> => {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() => publishTemplate(id)).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
        setTemplates: (list) => {
          this.state.setTemplates(list);
        },
        setCount: (count) => {
          this.state.setCount(count);
        },
        updateTemplateMetadata: (
          id: number,
          template: APILibraryTemplateMetadataRequestType,
        ): Observable<unknown> => {
          AppStore.instance.actions.isLoading(true);
          return requestToObservable(() => patchTemplate(id, template)).pipe(
            tap(() => AppStore.instance.actions.isLoading(false)),
            catchError((error) => {
              AppStore.instance.actions.isLoading(false);
              return throwError(() => error);
            }),
          );
        },
      },
      {
        useTemplateStoreService: (q) => {
          const { data, error, loading, query, setQuery } = useService(
            getTemplates,
            q,
          );
          useEffect(() => {
            if (error) {
              return;
            }
            if (!loading && data) {
              const pagination = parseLinkHeader(data.header);
              this.state.setTemplates(data?.data ?? []);
              this.state.setCount(pagination.count ?? data?.data?.length ?? 0);
            }
          }, [loading, data, error]);
          return { data, error, loading, query, setQuery };
        },
      },
    );
  }
}
